import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  DestroyRef,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
} from "@angular/core";
import { MatChipSelectionChange } from "@angular/material/chips";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { MentionTextareaComponent } from "./../expert-dialog/mention-textarea/mention-textarea.component";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { ListMentionsService } from "../../../data/mention/list-mentions.service";
import { UpdateMentionService } from "../../../data/mention/update-mention.service";

import { MentionDocument } from "../../../data/models/mention.model";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";

@Component({
  selector: "app-home-mention-list",
  templateUrl: "./home-mention-list.component.html",
  styleUrls: ["./home-mention-list.component.scss"],
  imports: SHARED_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeMentionListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly databaseCache = inject(DatabaseCacheService);
  private readonly listMentionsService = inject(ListMentionsService);
  private readonly updateMentionService = inject(UpdateMentionService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  readonly isLoading = signal(false);
  readonly isNotReadOnly = signal(false);
  readonly filterText = signal("");
  readonly searchExpanded = signal(false);
  readonly selectedMention = signal<MentionDocument | null>(null);

  private readonly mentionList = signal<MentionDocument[]>([]);

  // De-dup _loadData() calls: route.params and parametersUpdated often fire in
  // a burst. Same-section calls reuse the active request; section changes queue
  // one follow-up so stale responses never overwrite the latest view.
  private loadDataInflight: { key: string; promise: Promise<void> } | null = null;
  private pendingLoadDataKey: string | null = null;

  readonly filteredMentions = computed(() => {
    let list = this.mentionList();

    if (this.isNotReadOnly()) {
      list = list.filter((m) => !m.data.isRead);
    }

    const filter = this.filterText().trim().toLowerCase();
    if (filter) {
      list = list.filter((m) => {
        const searchStr = (
          m.type +
          m.data.selectedSectionName +
          m.data.mentionedContent +
          m.data.mentionFromUser.firstName +
          m.data.mentionFromUser.lastName +
          m.data.mentionToUser.firstName +
          m.data.mentionToUser.lastName +
          m.id
        ).toLowerCase();
        return searchStr.includes(filter);
      });
    }

    return [...list].sort((a, b) => {
      const dateA = new Date(a.commonInfo.lastModifiedAt).getTime();
      const dateB = new Date(b.commonInfo.lastModifiedAt).getTime();
      return dateB - dateA;
    });
  });

  readonly unreadCount = computed(
    () => this.mentionList().filter((m) => !m.data.isRead).length,
  );

  ngOnInit() {
    this.databaseCache.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        await this._loadData();
      });
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        await this._loadData();
      });
  }

  selectMention(mention: MentionDocument): void {
    this.selectedMention.set(mention);
  }

  isSelected(mention: MentionDocument): boolean {
    return this.selectedMention()?.id === mention.id;
  }

  getInitials(mention: MentionDocument): string {
    const lastName = mention.data.mentionFromUser.lastName;
    return lastName ? lastName.charAt(0) : "?";
  }

  getSenderName(mention: MentionDocument): string {
    return (
      mention.data.mentionFromUser.lastName +
      " " +
      mention.data.mentionFromUser.firstName
    );
  }

  genMentionPlainText(contentWithTags: string): string {
    return MentionTextareaComponent.genMentionPlainText(contentWithTags);
  }

  async navigateToMention(row: MentionDocument): Promise<void> {
    const targetSectionId = row.data.selectedSectionId;
    const targetUserId = row.data.selectedUserId;
    const targetMention = row;

    this.databaseCache.setSelectedSectionById(targetSectionId);
    let countTry = 0;
    while (true) {
      const changedSection = this.databaseCache.getSelectedSection();
      if (changedSection.sectionId == targetSectionId) {
        const usersList = this.databaseCache.getUsersList();
        const selectedUser = usersList?.find(
          (u) => u.user.userId == targetUserId,
        );
        if (selectedUser) {
          break;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      countTry++;
      if (countTry > 10) {
        console.error(
          `[${HomeMentionListComponent.name}] wait getSelectedSection().sectionId == targetSectionId / timeout / targetSectionId=${targetSectionId}`,
        );
        break;
      }
    }

    const navigateTo = [
      "home",
      targetSectionId,
      "users",
      targetUserId,
      "dialogs",
    ];

    this.router.navigate(navigateTo, {
      fragment: `dialog-content_${targetMention.data.dialogId}-${targetMention.data.responseIndex}`,
    });

    try {
      targetMention.data.isRead = true;
      await this.updateMentionService.updateMention(targetMention);
      await this.listMentionsService.isUnreadMentionsExist();
    } catch (error) {
      console.error(
        `[${HomeMentionListComponent.name}] fail updateMention / error=${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
      );
    }
  }

  // Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filterText().trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  applyFilter(event?: Event): void {
    if (event) {
      this.filterText.set((event.target as HTMLInputElement).value);
    }
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filterText.set("");
  }

  onUnreadFilterChange(event: MatChipSelectionChange): void {
    this.isNotReadOnly.set(event.selected);
  }

  private async _loadData(): Promise<void> {
    const key = this.databaseCache.getSelectedSection()?.sectionId;
    if (!key) {
      return;
    }

    if (this.loadDataInflight) {
      if (this.loadDataInflight.key !== key) {
        this.pendingLoadDataKey = key;
      }
      return this.loadDataInflight.promise;
    }

    const run = async (): Promise<void> => {
      this.isLoading.set(true);
      try {
        const mentionsList =
          await this.listMentionsService.listMentionsToCurrentLoginUser(null);
        if (this.databaseCache.getSelectedSection()?.sectionId === key) {
          this.mentionList.set(mentionsList);
        }
      } catch (error) {
        console.error(`[${HomeMentionListComponent.name}] fail listMentions`, error);
      } finally {
        if (this.pendingLoadDataKey == null) {
          this.isLoading.set(false);
        }
      }
    };

    const promise = run().finally(() => {
      const pendingKey = this.pendingLoadDataKey;
      this.loadDataInflight = null;
      this.pendingLoadDataKey = null;
      if (pendingKey && pendingKey !== key) {
        void this._loadData();
      }
    });

    this.loadDataInflight = { key, promise };
    return promise;
  }
}
